import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlayer } from '../contexts/PlayerContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MobileHeader } from './MobileHeader';
import { ArrowLeft, Users, TrendingUp, Clock, Trophy } from 'lucide-react';

interface QuestionSet {
  id: string;
  title: string;
  questionCount?: number;
  description?: string;
  state: 'open' | 'active' | 'closed';
}

interface Poll {
  id: string;
  title: string;
  question: string;
  options: Array<{
    id: string;
    emoji: string;
    label?: string;
  }>;
  state: 'open' | 'active' | 'closed';
  stayActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface GuessGame {
  id: string;
  title: string;
  description: string;
  correctAnswer: string;
  caseSensitive: boolean;
  mode: 'multiple' | 'collect';
  state: 'open' | 'active' | 'closed';
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

function PastGames() {
  const [questionSets, setQuestionSets] = useState<QuestionSet[]>([]);
  const [polls, setPolls] = useState<Poll[]>([]);
  const [guessGames, setGuessGames] = useState<GuessGame[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const { player, isAuthenticated } = usePlayer();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Fetch question sets and polls
  useEffect(() => {
    const fetchData = async () => {
      try {
        const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
        
        // Fetch question sets
        const questionSetsResponse = await fetch(`${serverUrl}/api/question-sets`);
        if (questionSetsResponse.ok) {
          const questionSetsData = await questionSetsResponse.json();
          // Filter only closed question sets
          const closedQuestionSets = (questionSetsData.questionSets || []).filter(
            (qs: QuestionSet) => qs.state === 'closed'
          );
          setQuestionSets(closedQuestionSets);
        } else {
          console.warn('Failed to fetch question sets');
          setQuestionSets([]);
        }

        // Fetch polls
        const pollsResponse = await fetch(`${serverUrl}/api/admin/polls`);
        if (pollsResponse.ok) {
          const pollsData = await pollsResponse.json();
          // Filter only closed polls
          const closedPolls = (pollsData.polls || []).filter(
            (poll: Poll) => poll.state === 'closed'
          );
          setPolls(closedPolls);
          console.log('📊 Fetched closed polls:', closedPolls);
        } else {
          console.warn('Failed to fetch polls');
          setPolls([]);
        }

        // Fetch guess games
        const guessGamesResponse = await fetch(`${serverUrl}/api/admin/guess-games`);
        if (guessGamesResponse.ok) {
          const guessGamesData = await guessGamesResponse.json();
          // Filter only closed guess games
          const closedGuessGames = (guessGamesData.guessGames || []).filter(
            (game: GuessGame) => game.state === 'closed'
          );
          setGuessGames(closedGuessGames);
          console.log('🎯 Fetched closed guess games:', closedGuessGames);
        } else {
          console.warn('Failed to fetch guess games');
          setGuessGames([]);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load past games. Please try again.');
        setQuestionSets([]);
        setPolls([]);
        setGuessGames([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleViewResults = (gameId: string, type: 'trivia' | 'poll' | 'guess') => {
    if (type === 'trivia') {
      navigate(`/leaderboard/${gameId}`);
    } else if (type === 'poll') {
      navigate(`/poll/${gameId}`);
    } else if (type === 'guess') {
      navigate(`/guess-leaderboard/${gameId}`);
    }
  };

  const handleBackToLobby = () => {
    navigate('/lobby');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading past games...</p>
        </div>
      </div>
    );
  }

  // Convert question sets to past trivia games format
  const pastTrivia = questionSets.map((questionSet) => ({
    id: questionSet.id,
    title: questionSet.title,
    description: questionSet.description || 'Test your knowledge',
    participants: Math.floor(Math.random() * 50) + 10, // Mock participants
    difficulty: questionSet.questionCount && questionSet.questionCount > 15 ? "Hard" : 
                 questionSet.questionCount && questionSet.questionCount > 10 ? "Medium" : "Easy",
    completedAt: "Recently completed", // Mock completion time
  }));

  // Convert polls to past polls format
  const pastPolls = polls.map((poll) => ({
    id: poll.id,
    title: poll.title,
    description: poll.question,
    participants: Math.floor(Math.random() * 200) + 50, // Mock participants
    options: poll.options.map(option => `${option.emoji} ${option.label || ''}`).slice(0, 4),
    completedAt: "Recently completed", // Mock completion time
  }));

  // Convert guess games to past guess games format
  const pastGuessGames = guessGames.map((game) => ({
    id: game.id,
    title: game.title,
    description: game.description,
    participants: Math.floor(Math.random() * 100) + 20, // Mock participants
    mode: game.mode,
    completedAt: "Recently completed", // Mock completion time
  }));

  return (
    <div className="min-h-screen bg-background">
      <MobileHeader
        isAuthenticated={isAuthenticated}
        user={player ? { name: player.nickname, email: user?.email || '' } : undefined}
        onSignInClick={() => {}}
        onSignOut={() => {}}
        showBackButton={true}
        onBackClick={handleBackToLobby}
        title="Past Games"
      />

      <main className="p-4 space-y-6">
        {/* Header Section */}
        <div className="text-center py-6">
          <h2 className="font-sans font-semibold text-2xl mb-2">Past Games</h2>
          <p className="text-muted-foreground font-mono">
            View results from completed trivia games and polls
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <Card className="mb-6 border-destructive/50 bg-destructive/5">
            <CardContent className="pt-6">
              <div className="flex">
                <div className="text-destructive">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-destructive">Error</h3>
                  <p className="text-sm text-destructive/80 mt-1">{error}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Past Trivia Games */}
        {pastTrivia.length > 0 && (
          <section>
            <div className="flex items-center mb-4">
              <h3 className="font-sans font-semibold text-xl">Completed Trivia</h3>
            </div>

            <div className="space-y-3">
              {pastTrivia.map((game) => (
                <Card key={game.id} className="relative">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <CardTitle className="font-sans font-semibold text-lg">{game.title}</CardTitle>
                          <Badge variant="outline" className="text-xs">
                            COMPLETED
                          </Badge>
                        </div>
                        <CardDescription className="font-mono">{game.description}</CardDescription>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        <span>{game.participants}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{game.completedAt}</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {game.difficulty}
                      </Badge>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <Button 
                      className="w-full" 
                      size="lg" 
                      variant="outline"
                      onClick={() => handleViewResults(game.id, 'trivia')}
                    >
                      <Trophy className="h-4 w-4 mr-2" />
                      View Results
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Past Polls */}
        {pastPolls.length > 0 && (
          <section>
            <div className="flex items-center mb-4">
              <h3 className="font-sans font-semibold text-xl">Completed Polls</h3>
            </div>

            <div className="space-y-3">
              {pastPolls.map((poll) => (
                <Card key={poll.id} className="relative">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <CardTitle className="font-sans font-semibold text-lg">{poll.title}</CardTitle>
                          <Badge variant="outline" className="text-xs">
                            COMPLETED
                          </Badge>
                        </div>
                        <CardDescription className="font-mono">{poll.description}</CardDescription>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <TrendingUp className="h-4 w-4" />
                      <span>{poll.participants} votes</span>
                      <div className="flex items-center gap-1 ml-2">
                        <Clock className="h-4 w-4" />
                        <span>{poll.completedAt}</span>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {poll.options.map((option, index) => (
                        <div key={index} className="border rounded-md p-3 text-center bg-muted/30 text-muted-foreground">
                          <span className="text-base">{option}</span>
                        </div>
                      ))}
                    </div>
                    <Button 
                      className="w-full" 
                      size="lg" 
                      variant="outline"
                      onClick={() => handleViewResults(poll.id, 'poll')}
                    >
                      <TrendingUp className="h-4 w-4 mr-2" />
                      View Results
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Past Guess Games */}
        {pastGuessGames.length > 0 && (
          <section>
            <div className="flex items-center mb-4">
              <h3 className="font-sans font-semibold text-xl">Completed Guess Games</h3>
            </div>

            <div className="grid gap-4">
              {pastGuessGames.map((game) => (
                <Card key={game.id} className="relative">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <CardTitle className="font-sans font-semibold text-lg">{game.title}</CardTitle>
                          <Badge variant="outline" className="text-xs">
                            COMPLETED
                          </Badge>
                        </div>
                        <CardDescription className="font-mono">{game.description}</CardDescription>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="h-4 w-4" />
                      <span>{game.participants} participants</span>
                      <Badge variant="outline" className="ml-2 text-xs">
                        {game.mode === 'multiple' ? 'Multiple Attempts' : 'One Attempt'}
                      </Badge>
                      <div className="flex items-center gap-1 ml-2">
                        <Clock className="h-4 w-4" />
                        <span>{game.completedAt}</span>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <Button 
                      className="w-full" 
                      size="lg" 
                      variant="outline"
                      onClick={() => handleViewResults(game.id, 'guess')}
                    >
                      <Trophy className="h-4 w-4 mr-2" />
                      View Leaderboard
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Empty State */}
        {pastTrivia.length === 0 && pastPolls.length === 0 && pastGuessGames.length === 0 && !isLoading && (
          <Card>
            <CardContent className="text-center py-12">
              <div className="text-muted-foreground mb-4">
                <Trophy className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">No past games yet</h3>
              <p className="text-muted-foreground mb-4">
                Completed trivia games, polls, and guess games will appear here.
              </p>
              <Button onClick={handleBackToLobby}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Lobby
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

export default PastGames;
